<template>
  <card-container
    :id="pageId"
    :loading-message="spinnerText"
    :is-loading="isLoading"
    :disabled="isLoading || !isPhoneNumberValid"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Create Gourmet Need Love
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Nature of Complaint* </label>
        <div class="col-sm-8">
          <dropdown-list
            id="complaintType"
            v-model="needLoveData.complaintType"
            name="complaintType"
            required
            filterable
            clearable
            :options="complaintTypeList"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Customer's Name* </label>
        <div class="col-sm-3">
          <sanitized-input
            id="customerName"
            v-model="needLoveData.customerName"
            name="customerName"
            required
          />
        </div>

        <label class="col-sm-2 control-label"> Phone Number* </label>
        <phone-number-input
          v-model="needLoveData.phoneNumber"
          class="col-sm-3"
          :page-id="pageId"
        />
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Caller ID* </label>
        <div class="col-sm-8">
          <sanitized-input
            id="callerId"
            v-model="needLoveData.callerId"
            name="callerId"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Restaurant Name* </label>
        <div class="col-sm-3">
          <sanitized-input
            id="restaurantName"
            v-model="needLoveData.restaurantName"
            name="restaurantName"
            required
          />
        </div>

        <label class="col-sm-2 control-label"> Cost* </label>
        <div class="col-sm-3">
          <el-input
            id="costOfOriginalOrder"
            v-model="needLoveData.costOfOriginalOrder"
            type="number"
            step="0.01"
            min="0.1"
            required
          >
            <template #prepend>
              $
            </template>
          </el-input>
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Order ID </label>
        <div class="col-sm-8">
          <sanitized-input
            id="orderId"
            v-model="needLoveData.orderId"
            name="orderId"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Vendor* </label>
        <div class="col-sm-3">
          <dropdown-list
            id="vendorType"
            v-model="needLoveData.vendorType"
            name="vendorType"
            required
            :options="vendorTypeList"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Slack Order Link* </label>
        <div class="col-sm-8">
          <sanitized-input
            id="slackOrderLink"
            v-model="needLoveData.slackOrderLink"
            name="slackOrderLink"
            required
          />
        </div>
      </div>

      <div class="row">
        <label class="col-sm-3 control-label"> Reason* </label>
        <div class="col-sm-8">
          <sanitized-textarea
            id="reason"
            v-model="needLoveData.reason"
            name="reason"
            autosize
            required
          />
        </div>
      </div>
    </template>
  </card-container>
</template>

<script lang="ts">
import { ref, Ref, computed, watch, onMounted, onUnmounted } from 'vue';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { ListType } from '@/models/app';
import { useGourmet } from '@/use/gourmet';
import { useProfile } from '@/use/profile';
import { needLoveService } from '@/services/needLove';
import { GourmetNeedLoveFormInput } from '@/models/needLove';

const pageId = 'create-gourmet-need-love';
const complaintTypeList: ListType[] = [
  { id: 1, key: 'Missing item(s)', label: 'Missing item(s)' },
  { id: 2, key: 'Wrong item(s)', label: 'Wrong item(s)' },
  { id: 3, key: 'Did not receive meal', label: 'Did not receive meal' },
  { id: 4, key: 'Delayed Order', label: 'Delayed Order' },
  { id: 5, key: 'Food Quality', label: 'Food Safety' },
  { id: 6, key: 'Driver did not follow instructions', label: 'Driver did not follow instructions' },
  { id: 7, key: 'Inconvenience', label: 'Refund for Manual Charge' },
  { id: 9, key: 'Canceled order', label: 'Canceled order' },
  { id: 10, key: 'Duplicate order', label: 'Duplicate order' },
  { id: 11, key: 'Special request not fulfilled', label: 'Special request not fulfilled' },
];
const vendorTypeList: ListType[] = [
  { id: 1, key: 'UberEats', label: 'UberEats' },
  { id: 2, key: 'DoorDash', label: 'DoorDash' },
];

export default {
  name: 'CreateGourmetNeedLove',

  setup: () => {
    const { customerInfo, customerFullName } = useCustomer();
    const { userFullName } = useProfile();
    const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();
    const { gourmetNeedLoveInfo, setGourmetNeedLoveInfo } = useGourmet();

    const needLoveData: Ref<GourmetNeedLoveFormInput> = ref({
      complaintType: '',
      customerName: '',
      phoneNumber: '',
      callerId: '',
      restaurantName: '',
      costOfOriginalOrder: 0,
      orderId: '',
      vendorType: '',
      operatorName: '',
      slackOrderLink: '',
      reason: '',
    });
    const isLoading = ref(false);
    const spinnerText = ref('');

    const isPhoneNumberValid = computed(() => {
      return util.isPhoneNumberValid(needLoveData.value.phoneNumber);
    });

    const submitForm = async () => {
      spinnerText.value = 'Submitting Gourmet Need Love...';
      isLoading.value = true;
      try {
        await needLoveService.postGourmetNLForm({
          ...needLoveData.value,
          costOfOriginalOrder: Number(needLoveData.value.costOfOriginalOrder),
        });

        await showSuccessPopupDialog({
          text: 'The Gourmet NeedLove request has been submitted successfully.',
        });

        resetForm();
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
      isLoading.value = false;
    };

    const resetForm = () => {
      needLoveData.value = {
        complaintType: '',
        customerName: gourmetNeedLoveInfo.value?.customerName ?? customerFullName.value ?? '',
        phoneNumber: gourmetNeedLoveInfo.value?.phoneNumber ?? customerInfo.value?.phoneNumber ?? '',
        callerId: gourmetNeedLoveInfo.value?.callerId ?? customerInfo.value?.id ?? '',
        restaurantName: gourmetNeedLoveInfo.value?.restaurantName,
        costOfOriginalOrder: gourmetNeedLoveInfo.value?.costOfOriginalOrder ?? 0,
        orderId: gourmetNeedLoveInfo.value?.orderId ?? '',
        vendorType: gourmetNeedLoveInfo.value?.vendorType ?? '',
        operatorName: userFullName.value,
        slackOrderLink: '',
        reason: '',
      };
    };

    watch(customerInfo, resetForm, {
      immediate: true,
    });

    onMounted(resetForm);

    onUnmounted(() => {
      setGourmetNeedLoveInfo(null);
    });

    return {
      pageId,
      complaintTypeList,
      vendorTypeList,
      needLoveData,
      isLoading,
      isPhoneNumberValid,
      spinnerText,

      util,

      submitForm,
      resetForm,
    };
  },
};
</script>
